/**
 * API Client Service
 * HTTP client for communicating with the backend API
 */

import { Effect } from "effect";
import type { AppError } from "@link-storage/common";
import { unknownError } from "@link-storage/common";

/**
 * Base API URL - uses environment variable for production
 */
const API_BASE_URL = import.meta.env["VITE_API_URL"] ?? "http://localhost:3000/api";

/**
 * Get auth headers with session token
 */
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("session_token");
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
};

/**
 * Generic fetch wrapper with Effect
 * 401 수신 시 /auth/refresh를 호출하고 원래 요청을 1회 재시도한다.
 * 재시도도 실패하면 'auth:session-expired' 이벤트를 발행하고 에러를 반환한다.
 */
export const fetchJson = <T>(
  path: string,
  options?: RequestInit
): Effect.Effect<T, AppError> =>
  Effect.tryPromise({
    try: async () => {
      const isRefreshRequest = path === "/auth/refresh";

      const makeRequest = () =>
        fetch(`${API_BASE_URL}${path}`, {
          ...options,
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
            ...options?.headers,
          },
        });

      let response = await makeRequest();

      if (response.status === 401 && !isRefreshRequest) {
        // 조용히 세션 갱신 시도
        const refreshResponse = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });

        if (!refreshResponse.ok) {
          // 갱신 실패 → 로그인 UI로 이동
          window.dispatchEvent(new CustomEvent("auth:session-expired"));
          throw new Error("Session expired");
        }

        // 새 쿠키가 자동으로 설정됨, 원래 요청 재시도
        response = await makeRequest();
      }

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          error: "UnknownError",
          message: `HTTP ${response.status}: ${response.statusText}`,
        }));
        throw new Error(error.message || "Request failed");
      }

      if (response.status === 204) {
        return undefined as T;
      }

      return await response.json() as T;
    },
    catch: (error) => {
      if (error instanceof Error) {
        return unknownError(error.message, error);
      }
      return unknownError("Unknown error occurred", error);
    },
  });

/**
 * GET request
 */
export const get = <T>(
  path: string,
  params?: Record<string, string>
): Effect.Effect<T, AppError> => {
  const url = params
    ? `${path}?${new URLSearchParams(params).toString()}`
    : path;
  return fetchJson<T>(url, { method: "GET" });
};

/**
 * POST request
 */
export const post = <T>(
  path: string,
  body: unknown
): Effect.Effect<T, AppError> =>
  fetchJson<T>(path, {
    method: "POST",
    body: JSON.stringify(body),
  });

/**
 * PUT request
 */
export const put = <T>(
  path: string,
  body: unknown
): Effect.Effect<T, AppError> =>
  fetchJson<T>(path, {
    method: "PUT",
    body: JSON.stringify(body),
  });

/**
 * DELETE request
 */
export const del = <T>(path: string): Effect.Effect<T, AppError> =>
  fetchJson<T>(path, { method: "DELETE" });
